import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection as firestoreCollection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { functions, db } from '../firebase';
import {
  Typography, TextField, MenuItem, Alert, Box, Button, Grid, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, List, ListItem, ListItemText,
} from '@mui/material';

const sendLocalityCoordinatorDigest = httpsCallable(functions, 'sendLocalityCoordinatorDigest');
const previewLocalityDigestRecipients = httpsCallable(functions, 'previewLocalityDigestRecipients');

const LEGACY_VIEW_OPTIONS = [{ id: 'sp26', mode: 'legacy', label: 'sp26 (legacy)' }];

function rowsFromDoc(data) {
  return (data?.localities || []).map((l) => ({
    locality: l.locality || '',
    coordinatorName: l.coordinatorName || '',
    coordinatorEmail: l.coordinatorEmail || '',
  }));
}

// Maintains the locality directory (settings/localityCoordinators - see
// firestore.rules) and sends each coordinator their own digest email (see
// sendLocalityCoordinatorDigest in functions/index.js), built from
// registrants_template.html, for whichever conference is selected below.
// `canEdit` gates the directory (same tier as Conference Settings);
// `canSend` gates the send button specifically (ADMIN/OWNER only - it
// emails real people outside the system).
export default function LocalityCoordinatorsPanel({ canEdit, canSend }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [viewOptions, setViewOptions] = useState(LEGACY_VIEW_OPTIONS);
  const [viewingId, setViewingId] = useState(null);

  // null = closed, 'all' = the bulk send, or a single recipient object = a
  // per-locality send (see the "Send" button next to each row below).
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Who would actually be emailed for the selected conference right now -
  // only localities with a coordinator email AND at least one registrant
  // (see previewLocalityDigestRecipients in functions/index.js). Drives the
  // button count and the confirm dialog's list, so what's shown always
  // matches what sending will actually do.
  const [recipients, setRecipients] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'localityCoordinators'));
        setRows(rowsFromDoc(snap.data()));
      } catch (error) {
        console.error('Failed to load locality coordinators:', error);
        setMessage({ type: 'error', text: error.message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [activeSnap, confsSnap] = await Promise.all([
          getDoc(doc(db, 'settings', 'active')),
          getDocs(firestoreCollection(db, 'conferences')),
        ]);
        const newOptions = confsSnap.docs.map((d) => ({ id: d.id, mode: 'new', label: d.id }));
        const options = [...newOptions, ...LEGACY_VIEW_OPTIONS];
        setViewOptions(options);
        const active = activeSnap.data()?.conferenceId;
        setViewingId(active || options[0]?.id || null);
      } catch (error) {
        console.error('Failed to load conference list:', error);
        setViewOptions(LEGACY_VIEW_OPTIONS);
        setViewingId(LEGACY_VIEW_OPTIONS[0].id);
      }
    })();
  }, []);

  const currentOption = viewOptions.find((o) => o.id === viewingId);

  async function loadRecipientsPreview() {
    if (!viewingId || !canSend) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const params = currentOption?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
      const res = await previewLocalityDigestRecipients(params);
      setRecipients(res.data?.recipients || []);
    } catch (error) {
      console.error('Failed to preview digest recipients:', error);
      setRecipients([]);
      setPreviewError(error.message);
    } finally {
      setPreviewLoading(false);
    }
  }

  useEffect(() => {
    loadRecipientsPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId, canSend]);

  function updateRow(index, field, value) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, { locality: '', coordinatorName: '', coordinatorEmail: '' }]);
  }

  function removeRow(index) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setMessage(null);
    try {
      const localities = rows
        .map((r) => ({
          locality: r.locality.trim(),
          coordinatorName: r.coordinatorName.trim(),
          coordinatorEmail: r.coordinatorEmail.trim(),
        }))
        .filter((r) => r.locality);
      await setDoc(doc(db, 'settings', 'localityCoordinators'), { localities });
      setRows(localities);
      setMessage({ type: 'success', text: 'Saved locality coordinators.' });
    } catch (error) {
      console.error('Failed to save locality coordinators:', error);
      setMessage({ type: 'error', text: error.message });
    }
  }

  const singleTarget = confirmTarget && confirmTarget !== 'all' ? confirmTarget : null;
  const confirmRecipients = singleTarget ? [singleTarget] : recipients;

  async function handleConfirmSend() {
    setSendBusy(true);
    setSendResult(null);
    try {
      const params = currentOption?.mode === 'legacy' ? { collection: viewingId } : { conferenceId: viewingId };
      if (singleTarget) params.locality = singleTarget.locality;
      const res = await sendLocalityCoordinatorDigest(params);
      setSendResult(res.data);
      setConfirmTarget(null);
    } catch (error) {
      console.error('Failed to send locality digest:', error);
      setSendResult({ sent: [], skipped: [], errors: [{ locality: singleTarget?.locality || '-', error: error.message }] });
    } finally {
      setSendBusy(false);
      loadRecipientsPreview();
    }
  }

  return (
    <Box sx={{ maxWidth: 900 }}>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have view-only access. Ask an admin to grant EDITOR or ADMIN access to save changes here.
        </Alert>
      )}
      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Typography variant="h6" gutterBottom>Locality coordinators</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One row per locality - the coordinator's email is who receives that locality's registrant digest below.
        A locality with no coordinator email won't receive anything, and its registrants will show up as
        "unassigned" in every digest that does go out.
      </Typography>

      {loading ? <Typography>Loading...</Typography> : (
        <>
          {rows.map((row, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField fullWidth label="Locality" value={row.locality}
                  onChange={(e) => updateRow(i, 'locality', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Coordinator name" value={row.coordinatorName}
                  onChange={(e) => updateRow(i, 'coordinatorName', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={10} sm={4}>
                <TextField fullWidth label="Coordinator email" value={row.coordinatorEmail}
                  onChange={(e) => updateRow(i, 'coordinatorEmail', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={2} sm={1}>
                <Button color="error" onClick={() => removeRow(i)} disabled={!canEdit}>Remove</Button>
              </Grid>
            </Grid>
          ))}
          <Button onClick={addRow} disabled={!canEdit}>+ Add locality</Button>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={!canEdit}>Save</Button>
          </Box>
        </>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" gutterBottom>Send registrant digest</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Emails each locality's coordinator their current registrants and total financial support owed, using
        registrants_template.html. A locality with no registrants yet is skipped.
      </Typography>

      <Box sx={{ mb: 2, maxWidth: 300, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          select fullWidth size="small" label="Conference"
          value={viewingId || ''}
          onChange={(e) => setViewingId(e.target.value)}
        >
          {viewOptions.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label || o.id}</MenuItem>
          ))}
        </TextField>
        {canSend && (
          <Button size="small" onClick={loadRecipientsPreview} disabled={previewLoading || !viewingId}>
            Refresh
          </Button>
        )}
      </Box>

      {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}

      <Button
        variant="contained" color="warning" onClick={() => { setSendResult(null); setConfirmTarget('all'); }}
        disabled={!canSend || !viewingId || previewLoading || recipients.length === 0}
      >
        {previewLoading ? 'Checking registrants...' : `Send digest to ${recipients.length} coordinator${recipients.length === 1 ? '' : 's'}`}
      </Button>
      {canSend && !previewLoading && viewingId && recipients.length === 0 && !previewError && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No localities have both a coordinator email and at least one registrant for this conference yet.
        </Typography>
      )}
      {!canSend && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Only an ADMIN can send the digest - ask one if you need it sent.
        </Typography>
      )}

      {canSend && recipients.length > 0 && (
        <List dense sx={{ mt: 2, maxWidth: 600 }}>
          {recipients.map((r, i) => (
            <ListItem
              key={i} disableGutters
              secondaryAction={
                <Button size="small" variant="outlined" onClick={() => { setSendResult(null); setConfirmTarget(r); }}>
                  Send
                </Button>
              }
            >
              <ListItemText
                primary={`${r.locality} (${r.count} registrant${r.count === 1 ? '' : 's'})`}
                secondary={`${r.coordinatorName ? `${r.coordinatorName} - ` : ''}${r.coordinatorEmail}`}
              />
            </ListItem>
          ))}
        </List>
      )}

      {sendResult && (
        <Box sx={{ mt: 2 }}>
          {sendResult.sent.length > 0 && (
            <Alert severity="success" sx={{ mb: 1 }}>
              Sent to {sendResult.sent.length} coordinator{sendResult.sent.length === 1 ? '' : 's'}:{' '}
              {sendResult.sent.map((s) => `${s.locality} (${s.email})`).join(', ')}
            </Alert>
          )}
          {sendResult.skipped?.length > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Skipped {sendResult.skipped.length}: {sendResult.skipped.map((s) => `${s.locality} - ${s.reason}`).join('; ')}
            </Alert>
          )}
          {sendResult.errors?.length > 0 && (
            <>
              <Alert severity="error" sx={{ mb: 1 }}>{sendResult.errors.length} failed to send:</Alert>
              <List dense>
                {sendResult.errors.map((e, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText primary={`${e.locality}${e.email ? ` (${e.email})` : ''}`} secondary={e.error} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      )}

      <Dialog open={!!confirmTarget} onClose={() => (sendBusy ? null : setConfirmTarget(null))}>
        <DialogTitle>
          {singleTarget
            ? `Send registrant digest to ${singleTarget.locality}?`
            : `Send registrant digest to ${recipients.length} coordinator${recipients.length === 1 ? '' : 's'}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This sends a real email to each of the following addresses, right now
            {singleTarget ? '' : ' - only localities with at least one registrant for this conference are included'}.
            This cannot be undone.
          </DialogContentText>
          <List dense>
            {confirmRecipients.map((r, i) => (
              <ListItem key={i} disableGutters>
                <ListItemText
                  primary={`${r.locality} (${r.count} registrant${r.count === 1 ? '' : 's'})`}
                  secondary={`${r.coordinatorName ? `${r.coordinatorName} - ` : ''}${r.coordinatorEmail}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={sendBusy}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmSend} disabled={sendBusy}>
            {sendBusy ? 'Sending...' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
